import { isPlatformBrowser, NgClass } from '@angular/common'
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Inject,
  inject,
  OnInit,
  PLATFORM_ID,
  signal,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'

import { TranslatePipe } from '@ngx-translate/core'
import { debounceTime, fromEvent, tap } from 'rxjs'

import { UiFacade } from '~/core/features'
import { ENotificationMessage, INotificationMessage } from '~/core/models'

@Component({
  selector: 'app-notification-modal',
  templateUrl: './notification-modal.component.html',
  styleUrls: ['./notification-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgClass, TranslatePipe, MatIconModule, MatButtonModule],
})
export class NotificationComponent implements OnInit {
  isMobile = false
  showNotifications = false
  ENotificationMessage = ENotificationMessage
  readonly notifications = signal<INotificationMessage[] | []>([])

  private notificationTimeout: any
  private readonly destroyRef = inject(DestroyRef)

  constructor(
    private uiFacade: UiFacade,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    afterNextRender(() => {
      this.subscribeToResize()
    })
  }

  ngOnInit(): void {
    this.uiFacade.notifications$.subscribe(data => {
      this.notifications.set(data)

      this.clearNotificationTimeout()
      this.startNotificationTimeout()
    })

    this.checkViewport()
  }

  checkViewport(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.isMobile = window.innerWidth <= 600
    }
    if (!this.isMobile) {
      this.showNotifications = true
    } else {
      this.showNotifications = false
    }
  }

  toggleNotifications(): void {
    this.showNotifications = !this.showNotifications
  }

  startNotificationTimeout(): void {
    this.notificationTimeout = setTimeout(() => {
      if (this.notifications() && this.notifications()?.length > 0) {
        this.closeNotification(0)
      }
    }, 2000)
  }

  clearNotificationTimeout(): void {
    if (this.notificationTimeout) {
      clearTimeout(this.notificationTimeout)
    }
  }

  onMouseEnter(): void {
    this.clearNotificationTimeout()
  }

  onMouseLeave(): void {
    this.startNotificationTimeout()
  }

  closeNotification(index: number): void {
    if (this.notifications() && index < this.notifications().length) {
      this.uiFacade.removeNotification(index)
    }
  }

  clearAllNotifications(): void {
    this.uiFacade.clearNotifications()
  }

  private subscribeToResize(): void {
    fromEvent(window, 'resize')
      .pipe(
        debounceTime(100),
        tap(() => {
          this.checkViewport()
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe()
  }
}
